import { observer } from 'mobx-react-lite';
import { PlusIcon, TrashIcon } from '@components/icons';
import type { StudyScene } from '@skjv/common';

interface SceneEditorProps {
  scenes: StudyScene[];
  onChange: (scenes: StudyScene[]) => void;
}

/**
 * Scene editor - manage topical breakdown of the passage.
 */
export const SceneEditor = observer(function SceneEditor({ scenes, onChange }: SceneEditorProps) {
  const handleAddScene = () => {
    onChange([...scenes, { descriptions: [''] }]);
  };

  const handleRemoveScene = (index: number) => {
    const newScenes = scenes.filter((_, i) => i !== index);
    onChange(newScenes);
  };

  const handleDescriptionChange = (sceneIndex: number, descIndex: number, value: string) => {
    const newScenes = scenes.map((scene, i) => {
      if (i !== sceneIndex) return scene;
      const newDescriptions = [...scene.descriptions];
      newDescriptions[descIndex] = value;
      return { ...scene, descriptions: newDescriptions };
    });
    onChange(newScenes);
  };

  const handleAddDescription = (sceneIndex: number) => {
    const newScenes = scenes.map((scene, i) => {
      if (i !== sceneIndex) return scene;
      return { ...scene, descriptions: [...scene.descriptions, ''] };
    });
    onChange(newScenes);
  };

  const handleRemoveDescription = (sceneIndex: number, descIndex: number) => {
    const newScenes = scenes.map((scene, i) => {
      if (i !== sceneIndex) return scene;
      const newDescriptions = scene.descriptions.filter((_, j) => j !== descIndex);
      return { ...scene, descriptions: newDescriptions.length ? newDescriptions : [''] };
    });
    onChange(newScenes);
  };

  return (
    <div className="rounded-xl border border-stone-200/50 bg-stone-50/50 p-3 dark:border-stone-700/30 dark:bg-stone-800/30">
      <h3 className="mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">
        Scenes ({scenes.length})
      </h3>

      <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">
        Break the passage into digestible segments. Kids will draw, adults take notes.
      </p>

      {scenes.length === 0 ? (
        <div className="py-4 text-center text-sm text-stone-400 dark:text-stone-500">
          No scenes yet. Add one to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {scenes.map((scene, sceneIndex) => (
            <div
              key={sceneIndex}
              className="rounded-lg border border-stone-200/30 bg-white/50 p-2 dark:border-stone-700/20 dark:bg-stone-900/30"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-stone-600 dark:text-stone-400">
                  Scene {sceneIndex + 1}
                </span>
                <button
                  onClick={() => handleRemoveScene(sceneIndex)}
                  className="p-1 text-stone-400 transition-colors hover:text-red-500"
                  title="Remove scene"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="space-y-2">
                {scene.descriptions.map((desc, descIndex) => (
                  <div key={descIndex} className="flex gap-1">
                    <input
                      type="text"
                      value={desc}
                      onChange={(e) =>
                        handleDescriptionChange(sceneIndex, descIndex, e.target.value)
                      }
                      placeholder={`Point ${descIndex + 1}...`}
                      className="flex-1 rounded border border-stone-300/50 bg-white/80 px-2 py-1.5 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 dark:border-stone-600/50 dark:bg-stone-800/60 dark:text-stone-200 dark:placeholder-stone-500"
                    />
                    {scene.descriptions.length > 1 && (
                      <button
                        onClick={() => handleRemoveDescription(sceneIndex, descIndex)}
                        className="p-1 text-stone-400 transition-colors hover:text-red-500"
                        title="Remove point"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleAddDescription(sceneIndex)}
                className="mt-2 text-xs text-stone-500 transition-colors hover:text-cyan-600 dark:hover:text-cyan-400"
              >
                + Add point
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleAddScene}
        className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-stone-300/50 px-2 py-2 text-xs text-cyan-600 transition-colors hover:bg-cyan-100/50 dark:border-stone-600/30 dark:text-cyan-400 dark:hover:bg-cyan-900/30"
      >
        <PlusIcon className="h-3.5 w-3.5" />
        Add Scene
      </button>
    </div>
  );
});
